import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, IconButton } from '@material-ui/core';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import useVrackApi from '../../../hooks/useVrackApi';

const PowerStatus = {
  DISABLED: 'disabled',
  RUNNING: 'running',
  STOPPED: 'stopped'
};

SetRackPowerButton.propTypes = {
  rackDetails: PropTypes.object.isRequired,
  jobInProgress: PropTypes.bool.isRequired,
  type: PropTypes.object.isRequired, // lru or vm
  endpoint: PropTypes.string.isRequired,
  buildPayload: PropTypes.func.isRequired, // type-specific payload builder
  label: PropTypes.string.isRequired, // what to show in UI (LRU, VM, etc.)
  sx: PropTypes.object
};

export default function SetRackPowerButton({
  rackDetails,
  jobInProgress,
  type,
  endpoint,
  buildPayload,
  label,
  sx = {}
}) {
  const dispatch = useDispatch();
  const identity = useSelector((state) => state.identity.value);
  const { fetchData } = useVrackApi('post', endpoint);

  const [power, setPower] = useState(PowerStatus.DISABLED);
  const [error, setError] = useState(null);

  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  const getDesiredRunningState = () => power !== PowerStatus.RUNNING;

  const getDesiredPowerStateLabel = () => (power === PowerStatus.RUNNING ? 'off' : 'on');

  useEffect(() => {
    if (rackDetails.running?.state !== PowerStatus.RUNNING) {
      setPower(PowerStatus.DISABLED);
    } else if (type.running?.state !== PowerStatus.STOPPED) {
      setPower(PowerStatus.RUNNING);
    } else {
      setPower(PowerStatus.STOPPED);
    }
  }, [rackDetails, type, error]);

  const getButtonColor = () => (power === PowerStatus.RUNNING ? 'success' : 'secondary');
  const handlePowerToggle = async () => {
    try {
      const payload = {
        environment: rackDetails.region,
        identity,
        releaseName: rackDetails.name,
        ...buildPayload(type),
        running: getDesiredRunningState()
      };

      await fetchData({ data: payload });

      openSnackbar({
        message: `Request to set rack ${label} running state sent successfully!`
      });
    } catch (err) {
      openSnackbar({
        message: err?.response?.data?.message || 'Something went wrong',
        severity: 'error'
      });
      setError(err);
    }
  };

  return (
    <Tooltip title={`Turn ${getDesiredPowerStateLabel()} ${label}`} placement="top">
      <div>
        <IconButton
          size="small"
          disabled={jobInProgress || power === PowerStatus.DISABLED}
          onClick={handlePowerToggle}
          sx={sx}
          color={getButtonColor()}
        >
          <PowerSettingsNewOutlinedIcon />
        </IconButton>
      </div>
    </Tooltip>
  );
}
